import { FormattedMessage, useIntl } from "react-intl";

import { Text } from "@/components/atoms/Text";
import InstantBookIcon from "@/icons/__generated__/InstantBookSlimFilled/InstantBookSlimFilled28";
import { ListingData, useListingData } from "~/context/ListingPageDataContext";
import { normalizePriceRange } from "~/utils/normalizePriceRange";

import { useBookListingContext } from "../../BookListingContext";

import { DayRateDiscount } from "./DayRateDiscount";
import { FsvBookListingCardHeader } from "./FsvBookListingCardHeader";
import { PriceFeeCalcSkeleton } from "./PriceFeeCalcSkeleton";

export const BookListingCardHeader = () => {
  const intl = useIntl();

  const { data: listingData = {} as ListingData } = useListingData();
  const { quote, isFetchingQuote } = useBookListingContext();
  const { guestPaymentDetails, spaceRentalContract } = quote || {};
  const { booking } = spaceRentalContract?.meta || {};

  const formatPrice = (value: number) => {
    return intl.formatNumber(value, {
      style: "currency",
      currency: listingData.listingCurrency,
      minimumFractionDigits: value % 1 === 0 ? 0 : 2,
      maximumFractionDigits: value % 1 === 0 ? 0 : 2,
    });
  };

  const bookingTotal = guestPaymentDetails?.subtotal_remaining ?? 0;
  const ratePrice = booking?.rate || listingData.listingPrice;

  const { min, max, isTotal, isRange } = normalizePriceRange(
    listingData.listingPriceRange,
    booking?.rate || null,
    bookingTotal,
  );

  const price =
    isRange && min && max
      ? `${formatPrice(min)}–${formatPrice(max)}`
      : isTotal
        ? formatPrice(bookingTotal)
        : formatPrice(ratePrice);

  const showMinimumDuration =
    +listingData.listingMinimumDuration > 0 && !isTotal;

  const showDayRateDiscount = !!listingData?.listingDayRateDiscount?.value;

  if (listingData.fullService?.active) {
    return <FsvBookListingCardHeader />;
  }

  return (
    <div className="tw-border-b tw-border-gray-200">
      <div className="tw-flex tw-flex-col tw-items-center tw-p-24">
        {isFetchingQuote && <PriceFeeCalcSkeleton />}

        {!isFetchingQuote && (
          <>
            {!booking?.rate && (
              <Text
                typeface="body-1-long"
                className="tw-text-purple-600 tw-mb-4"
              >
                <FormattedMessage defaultMessage="Add details to view total price" />
              </Text>
            )}

            <div className="tw-flex tw-items-center">
              {listingData.isInstantBookActive && (
                <span data-testing-id="instantBookingLightningSign">
                  <InstantBookIcon />
                </span>
              )}
              <span>
                <Text typeface="headline-4-b" data-testing-id="hourPriceLabel">
                  {price}
                </Text>
                {!isTotal && (
                  <Text typeface="headline-1-m" className="tw-ml-4">
                    <FormattedMessage defaultMessage="/hr" />
                  </Text>
                )}
              </span>
            </div>

            {showMinimumDuration && (
              <div className="tw-flex tw-items-center tw-gap-8">
                <Text
                  testId="minimum-duration-label"
                  typeface="body-1-long"
                  className="tw-text-gray-600"
                >
                  <FormattedMessage
                    defaultMessage="{minimum_duration} hr minimum"
                    values={{
                      minimum_duration: listingData.listingMinimumDuration,
                    }}
                  />
                </Text>
              </div>
            )}

            {isTotal && (
              <Text
                testId="estimated-total-label"
                typeface="body-1-long"
                className="tw-text-gray-600"
              >
                <FormattedMessage defaultMessage="Estimated total" />
              </Text>
            )}
          </>
        )}
      </div>

      {showDayRateDiscount && (
        <DayRateDiscount
          price={booking?.rate || listingData.listingPrice}
          currency={listingData.listingCurrency}
          discount={listingData.listingDayRateDiscount}
        />
      )}
    </div>
  );
};
